/**
 * Data transformation utilities for API communication
 *
 * Handles case conversion between frontend (camelCase) and backend (snake_case),
 * and value format conversions (percentages to decimals, etc.)
 */

/**
 * Convert camelCase string to snake_case
 */
export function toSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

/**
 * Convert snake_case string to camelCase
 */
export function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Transform object keys from camelCase to snake_case
 * Recursively transforms nested objects (but not arrays or dates)
 */
export function transformToSnakeCase<T extends Record<string, unknown>>(
  obj: T,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key in obj) {
    if (Object.hasOwn(obj, key)) {
      const snakeKey = toSnakeCase(key);
      const value = obj[key];
      if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
        result[snakeKey] = transformToSnakeCase(value as Record<string, unknown>);
      } else {
        result[snakeKey] = value;
      }
    }
  }
  return result;
}

/**
 * Transform object keys from snake_case to camelCase
 * Recursively transforms nested objects (but not arrays or dates)
 */
export function transformToCamelCase<T extends Record<string, unknown>>(
  obj: T,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key in obj) {
    if (Object.hasOwn(obj, key)) {
      const camelKey = toCamelCase(key);
      const value = obj[key];
      if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
        result[camelKey] = transformToCamelCase(value as Record<string, unknown>);
      } else {
        result[camelKey] = value;
      }
    }
  }
  return result;
}

/**
 * Convert percentage values to decimal format
 * Frontend uses percentages (e.g., 6.5 for 6.5%)
 * Backend expects decimals (e.g., 0.065 for 6.5%)
 *
 * @param obj - Object containing the values to convert
 * @param percentageFields - Array of field names to convert
 */
export function convertPercentagesToDecimal(
  obj: Record<string, unknown>,
  percentageFields: string[],
): Record<string, unknown> {
  const result = { ...obj };
  for (const field of percentageFields) {
    const value = result[field];
    if (typeof value === 'number') {
      result[field] = value / 100;
    } else if (typeof value === 'string' && value !== '') {
      // Handle string values (e.g., from form inputs)
      const numValue = Number.parseFloat(value);
      if (!Number.isNaN(numValue)) {
        result[field] = numValue / 100;
      }
    }
  }
  return result;
}

/**
 * Convert decimal values to percentage format
 * Backend stores decimals (e.g., 0.065 for 6.5%)
 * Frontend displays percentages (e.g., 6.5 for 6.5%)
 *
 * Note: Sequelize returns DECIMAL fields as strings to preserve precision.
 * This function handles both number and string values.
 *
 * @param obj - Object containing the values to convert
 * @param decimalFields - Array of field names to convert
 */
export function convertDecimalsToPercentage(
  obj: Record<string, unknown>,
  decimalFields: string[],
): Record<string, unknown> {
  const result = { ...obj };
  for (const field of decimalFields) {
    const value = result[field];
    if (typeof value === 'number') {
      result[field] = value * 100;
    } else if (typeof value === 'string' && value !== '') {
      // Handle string values from Sequelize DECIMAL fields
      const numValue = Number.parseFloat(value);
      if (!Number.isNaN(numValue)) {
        result[field] = numValue * 100;
      }
    }
  }
  return result;
}

/**
 * Convert string DECIMAL fields to numbers
 * Sequelize returns DECIMAL/NUMERIC fields as strings to preserve precision.
 * This function converts specified fields from strings to numbers.
 *
 * @param obj - Object containing the values to convert
 * @param numericFields - Array of field names to convert to numbers
 */
export function convertDecimalStringsToNumbers(
  obj: Record<string, unknown>,
  numericFields: string[],
): Record<string, unknown> {
  const result = { ...obj };
  for (const field of numericFields) {
    const value = result[field];
    if (typeof value === 'string' && value !== '') {
      const numValue = Number.parseFloat(value);
      if (!Number.isNaN(numValue)) {
        result[field] = numValue;
      }
    }
  }
  return result;
}
